import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import {
  getSections,
  createSection,
  updateSection,
  deleteSection,
  getSectionById,
} from '../services/sectionService';
import Notification from '../components/Common/Notification';

export const SectionContext = createContext();

export const SectionProvider = ({ children }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [notification, setNotification] = useState({ message: '', isError: false });

  const fetchAuthHeaders = () => {
    const storedUser = localStorage.getItem('user');
    const token = user?.token || (storedUser ? JSON.parse(storedUser).token : '');

    if (!token) {
      console.error('User or token not available');
      return {};
    }

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const showNotification = (message, isError) => {
    setNotification({ message, isError });
  };

  const closeNotification = () => {
    setNotification({ message: '', isError: false });
  };

  const fetchAllSections = async () => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getSections(authHeaders);
      setSections(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error fetching sections.';
      showNotification(errorMessage, true);
    }
  };

  const fetchSectionById = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getSectionById(id, authHeaders);
      setSelectedSection(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching section with ID ${id}.`;
      showNotification(errorMessage, true);
    }
  };

  const addSection = async (sectionData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const newSection = await createSection(sectionData, authHeaders);
      setSections(prevSections => [...prevSections, newSection]);
      showNotification('Section added successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error adding section.';
      showNotification(errorMessage, true);
    }
  };

  const editSection = async (id, sectionData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const updatedSection = await updateSection(id, sectionData, authHeaders);
      setSections(prevSections =>
        prevSections.map(section => (section.id === id ? updatedSection : section))
      );
      showNotification('Section updated successfully!', false);
      return updatedSection;
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error updating section.';
      showNotification(errorMessage, true);
      throw error;
    }
  };

  const removeSection = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      await deleteSection(id, authHeaders);
      setSections(prevSections => prevSections.filter(section => section.id !== id));
      showNotification('Section removed successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error removing section.';
      showNotification(errorMessage, true);
    }
  };

  // useEffect(() => {
  //   fetchAllSections();
  // }, []);


  useEffect(() => {
    if (!isAuthenticated && !['/', '/login'].includes(window.location.pathname)) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);


  return (
    <SectionContext.Provider
      value={{
        sections,
        selectedSection,
        fetchAllSections,
        fetchSectionById,
        addSection,
        editSection,
        removeSection,
      }}
    >
      {children}
      {notification.message && (
        <Notification
          message={notification.message}
          isError={notification.isError}
          onClose={closeNotification}
        />
      )}
    </SectionContext.Provider>
  );
};
